import { useContext } from "react";
import type { LocalizedText } from "@/shared/api/types";
import { cx } from "@/shared/lib/cx";
import { UI_LOCALES } from "@/shared/lib/format";
import { FieldGroup } from "./Field";
import { Input, Textarea } from "./controls";
import { LocalizedInputActionContext } from "./localizedInputAction";

const LOCALE_NAME: Record<string, string> = { vi: "Tiếng Việt", en: "English" };

/** Edits a LocalizedText map for the supported UI locales (vi required, others optional). */
export function LocalizedInput({ label, value, onChange, error, multiline, locales = UI_LOCALES, required, hint, rows = 2 }: {
  label: string; value: LocalizedText; onChange: (v: LocalizedText) => void; error?: string; multiline?: boolean;
  locales?: readonly string[]; required?: boolean; hint?: string; rows?: number;
}) {
  const renderAction = useContext(LocalizedInputActionContext);
  return (
    <FieldGroup label={label} error={error} required={required} hint={hint}
      actions={renderAction?.({ label, value, onChange, locales })}>
      {locales.map((loc) => {
        const props = {
          value: value[loc] ?? "",
          lang: loc,
          "aria-label": `${label} (${LOCALE_NAME[loc] ?? loc})`,
          "aria-invalid": loc === "vi" && !!error,
          placeholder: loc === "vi" ? `${LOCALE_NAME.vi}${required ? " (bắt buộc)" : ""}` : `${LOCALE_NAME[loc] ?? loc} (tuỳ chọn)`,
          onChange: (e: { target: { value: string } }) => onChange({ ...value, [loc]: e.target.value }),
        };
        return (
          <div key={loc} className={cx("locale-input", multiline && "is-multiline")}>
            <span className="locale-tag" title={LOCALE_NAME[loc]}>{loc}</span>
            {multiline ? <Textarea rows={rows} {...props} /> : <Input {...props} />}
          </div>
        );
      })}
    </FieldGroup>
  );
}
